import { useCallback, useRef, useState } from 'react';
import { TextLayoutEvent } from 'react-native';

import { isTextClipped } from '@/utils/is-text-clipped';
import { TextSplit, splitTextAtWordBoundary } from '@/utils/split-text-at-word-boundary';

/**
 * Hook to handle text overflow by splitting text across multiple lines.
 * When text wraps to multiple lines, it captures line1 and line2 separately
 * so they can be rendered with different styling if needed.
 *
 * The split is snapped to a word boundary so the first line never cuts a word in half (see
 * `splitTextAtWordBoundary`). The full text is always rendered for measurement (hidden when split is
 * active), so that layout changes (e.g. an icon appearing) trigger a re-split.
 */
export const useTextOverflowSplit = (text: string) => {
    const [textSplit, setTextSplit] = useState<TextSplit | null>(null);
    const prevLine1Ref = useRef<string | null>(null);

    const handleTextLayout = useCallback(
        (e: TextLayoutEvent) => {
            const { lines } = e.nativeEvent;

            if (isTextClipped(e)) {
                const split = splitTextAtWordBoundary(text, lines[0].text.length);
                if (split.line1 !== prevLine1Ref.current) {
                    prevLine1Ref.current = split.line1;
                    setTextSplit(split);
                }
            } else if (prevLine1Ref.current !== null) {
                // Text no longer overflows, clear the split
                prevLine1Ref.current = null;
                setTextSplit(null);
            }
        },
        [text]
    );

    return {
        textSplit,
        handleTextLayout,
        hasOverflow: textSplit !== null,
    };
};
